import { useState } from "react";
import AlertsHistorySidebar, { AlertsHistoryFilterKey } from "@components/Sidebar/AlertsHistory";
import AlertsSummaryCard from "@components/shared/AlertsSummaryCard";
import AlertsRecentCardSkeleton from "@components/Skeletons/AlertsRecentCardSkeleton";
import { useT } from "@hooks";

export interface RecentAlertItem {
  id: string;
  label: string;
  count: number;
}

export interface RecentAlertsSummary {
  totalCount: number;
  items: RecentAlertItem[];
}

interface AlertsRecentCardProps {
  data?: RecentAlertsSummary;
}

const ITEM_FILTER_MAP: Record<string, AlertsHistoryFilterKey> = {
  "tank-capacity-exceeded": "abastecimento",
  "abnormal-consumption": "consumo",
  "fuel-price-above-average": "valor",
};

export default function AlertsRecentCard({ data }: Readonly<AlertsRecentCardProps>) {
  const t = useT();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [initialFilter, setInitialFilter] = useState<AlertsHistoryFilterKey>("todos");

  const openHistory = (filter: AlertsHistoryFilterKey) => {
    setInitialFilter(filter);
    setIsHistoryOpen(true);
  };

  const DEFAULT_ITEMS: RecentAlertItem[] = [
    {
      id: "tank-capacity-exceeded",
      label: t("fueling.alert.aboveTankCapacity"),
      count: 0,
    },
    {
      id: "abnormal-consumption",
      label: t("fueling.alert.abnormalConsumption"),
      count: 0,
    },
    {
      id: "fuel-price-above-average",
      label: t("fueling.alert.aboveFleetAvgPrice"),
      count: 0,
    },
  ];

  if (!data) {
    return <AlertsRecentCardSkeleton items={DEFAULT_ITEMS.length} />;
  }

  const itemsById = new Map(data.items.map((item) => [item.id, item]));
  const displayItems = DEFAULT_ITEMS.map((defaultItem) => itemsById.get(defaultItem.id) ?? defaultItem);

  return (
    <AlertsSummaryCard
      title={t("dashboard.alerts.title")}
      historyLabel={t("dashboard.alerts.history")}
      totalCount={data.totalCount}
      items={displayItems}
      onItemClick={(id) => openHistory(ITEM_FILTER_MAP[id])}
      onHistoryClick={() => openHistory("todos")}
    >
      <AlertsHistorySidebar
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        initialFilter={initialFilter}
      />
    </AlertsSummaryCard>
  );
}
